import * as React from "react";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { pullConflictsFromMagento, syncActiveSheetWithMagento, syncDiffsToMagento, syncProductsToWorkbookPages } from "../services/excel";
import { clearDiffs, clearSheetDiffs, upsertDiffRow } from "../slices/diffSlice";

const TextInsertion: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isPulling, setIsPulling] = useState<boolean>(false);
  const [isForcePushing, setIsForcePushing] = useState<boolean>(false);
  const [syncProgress, setSyncProgress] = useState<{ fetched: number; total: number } | null>(null);

  const dispatch = useAppDispatch();
  const diffRows = useAppSelector((state) => Object.values(state.diffs.rows));
  const activeSheetId = useAppSelector((state) => state.excel.activeSheetId);
  const sheetDiffRows = useMemo(
    () => diffRows.filter((row) => row.sheetId === activeSheetId),
    [diffRows, activeSheetId]
  );

  const validEdits = useMemo(() => sheetDiffRows.filter((r) => !r.isOutOfSync), [sheetDiffRows]);
  const conflicts = useMemo(() => sheetDiffRows.filter((r) => r.isOutOfSync), [sheetDiffRows]);

  const diffCount = validEdits.length;
  const accounts = useAppSelector((state) => state.accounts.accounts);
  const activeAccountId = useAppSelector((state) => state.accounts.activeAccountId);
  const activeAccount = useMemo(
    () => accounts.find((account) => account.id === activeAccountId) || null,
    [accounts, activeAccountId]
  );
  const hasActiveAccount = Boolean(activeAccount);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSyncProgress(null);
    try {
      if (!activeAccount) return;
      await syncProductsToWorkbookPages(activeAccount, 100, (fetched, total) => {
        setSyncProgress({ fetched, total });
      });
    } catch (error) {
      console.error("Failed to sync or fetch products:", error);
    } finally {
      setIsSubmitting(false);
      setSyncProgress(null);
    }
  };

  const handleRefreshSheet = async () => {
    setIsRefreshing(true);
    try {
      if (!activeAccount) return;
      await syncActiveSheetWithMagento(activeAccount);
    } catch (error) {
      console.error("Failed to sync sheet with Magento:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSyncToMagento = async () => {
    setIsSyncing(true);
    const pendingDiffs = validEdits;
    if (activeSheetId) {
      dispatch(clearSheetDiffs(activeSheetId));
    } else {
      dispatch(clearDiffs());
    }

    // Restore conflicts to state so they aren't lost when we clear the sheet diffs
    conflicts.forEach((diff) => dispatch(upsertDiffRow(diff)));

    try {
      if (!activeAccount || !activeSheetId) return;
      const { failedRows } = await syncDiffsToMagento(activeAccount, activeSheetId, pendingDiffs);
      if (failedRows.length) {
        failedRows.forEach((diff) => dispatch(upsertDiffRow(diff)));
      }
    } catch (error) {
      console.error("Failed to sync diffs to Magento:", error);
      pendingDiffs.forEach((diff) => dispatch(upsertDiffRow(diff)));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleForcePush = async () => {
    setIsForcePushing(true);
    // Remove the outOfSync flag so it gets pushed
    const forcedDiffs = conflicts.map((c) => ({ ...c, isOutOfSync: false }));

    if (activeSheetId) {
      dispatch(clearSheetDiffs(activeSheetId));
    } else {
      dispatch(clearDiffs());
    }

    validEdits.forEach((diff) => dispatch(upsertDiffRow(diff)));

    try {
      if (!activeAccount || !activeSheetId) return;
      const { failedRows } = await syncDiffsToMagento(activeAccount, activeSheetId, forcedDiffs);
      if (failedRows.length) {
        failedRows.forEach((diff) => dispatch(upsertDiffRow(diff)));
      }
    } catch (error) {
      console.error("Failed to force push diffs:", error);
      forcedDiffs.forEach((diff) => dispatch(upsertDiffRow(diff)));
    } finally {
      setIsForcePushing(false);
    }
  };

  const handlePullConflicts = async () => {
    setIsPulling(true);
    try {
      if (!activeAccount || !activeSheetId) return;
      await pullConflictsFromMagento(activeAccount, activeSheetId, conflicts);
    } catch (error) {
      console.error("Failed to pull conflicts:", error);
    } finally {
      setIsPulling(false);
    }
  };

  return (
    <section className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="mb-4"></div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <p className="text-sm text-slate-600">
          Fetch products from Magento and insert 100 items per sheet (Sheet 1 = Page 1, Sheet 2 = Page 2).
        </p>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap justify-between gap-3">
            {!hasActiveAccount && (
              <p className="text-xs text-rose-500">Select a store connection to enable sync.</p>
            )}
            <div className="flex flex-wrap justify-end gap-3 w-full">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                onClick={handleRefreshSheet}
                disabled={!hasActiveAccount || isRefreshing}
              >
                {isRefreshing
                  ? "Syncing..."
                  : `Sync sheet with Magento`}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                onClick={handleSyncToMagento}
                disabled={!hasActiveAccount || isSyncing || diffCount === 0}
              >
                {isSyncing
                  ? "Syncing..."
                  : `Sync valid edits to Magento${diffCount ? ` (${diffCount})` : ""}`}
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-400"
                disabled={!hasActiveAccount || isSubmitting}
              >
                {isSubmitting
                  ? (syncProgress ? `Syncing... ${syncProgress.fetched} / ${syncProgress.total}` : "Syncing...")
                  : "Sync products to Excel"}
              </button>
            </div>
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-md bg-rose-50 p-4 ring-1 ring-rose-200">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-rose-800">
                    {conflicts.length} Conflict{conflicts.length > 1 ? "s" : ""} Detected
                  </h3>
                  <div className="mt-2 text-sm text-rose-700">
                    <p>Some rows (marked in red) were modified in Magento by someone else. You can forcefully overwrite their changes, or pull their changes down to your sheet.</p>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                      onClick={handleForcePush}
                      disabled={isForcePushing}
                    >
                      {isForcePushing ? "Pushing..." : "Force Overwrite Magento"}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                      onClick={handlePullConflicts}
                      disabled={isPulling}
                    >
                      {isPulling ? "Pulling..." : "Pull from Magento"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </section>
  );
};

export default TextInsertion;
